<template>
  <WithBackdrop
    :class="[$style.config, $style.wrap]"
    :backdrop="data?.fields?.backdrop"
  >
    <div :class="$style.inner">
      <div :class="$style.layout">
        <div :class="$style.cardContainer">
          <AspectRatio
            v-if="data?.fields?.media"
            :ratio="`${data?.fields?.media?.fields?.file?.details?.image?.width}x${data?.fields?.media?.fields?.file?.details?.image?.height}`"
          >
            <BasePicture
              v-if="data?.fields?.media?.fields?.file?.url"
              :src="data?.fields?.media?.fields?.file?.url"
              :alt="data?.fields?.media?.fields?.description || ''"
            />
          </AspectRatio>
        </div>
        <div :class="$style.contentContainer">
          <BaseHeading
            v-if="data?.fields?.header"
            :class="$style.header"
            size="h3"
          >
            {{ data?.fields?.header }}
          </BaseHeading>
          <MarkedParser
            :md="data?.fields?.subheader"
            :class="$style.subheader"
          />
          <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />

          <div v-if="computedReferences.length" :class="$style.cards">
            <div
              v-for="a in computedReferences"
              :key="a?.sys?.id"
              :class="$style.cardLayout"
            >
              <AspectRatio
                v-if="a?.fields?.image"
                :ratio="`${a?.fields?.image?.fields?.file?.details?.image?.width}x${a?.fields?.image?.fields?.file?.details?.image?.height}`"
              >
                <BasePicture
                  :src="`${a?.fields?.image?.fields?.file?.url}?w=36&q=70`"
                  :alt="a?.fields?.image?.fields?.description || ''"
                  width="36"
                  loading="lazy"
                />
              </AspectRatio>
              <div>
                <p v-if="a?.fields?.header" :class="$style.mediaHeader">
                  {{ a?.fields?.header }}
                </p>
                <MarkedParser :md="a?.fields?.copy" :class="$style.mediaCopy" />
              </div>
            </div>
          </div>

          <div v-if="data?.fields?.ctas?.length">
            <CallToAction
              v-for="c in data?.fields?.ctas"
              :key="c?.sys?.id"
              :cta="c"
            />
          </div>
        </div>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

// Computed
const computedReferences = computed(
  () => props.data?.fields?.references?.filter?.(instanceOfMedia) || [],
)
</script>

<style lang="scss" module>
.wrap {
  position: relative;
}

.inner {
  max-width: 1350px;
  padding: 67px 20px 82px;
  margin: auto;
}

.layout {
  display: grid;
  gap: 50px;
  align-items: flex-start;

  @include media('sm') {
    grid-template-columns: 60% 1fr;
    gap: 30px;
  }

  @include media('md') {
    grid-template-columns: minmax(28%, 450px) 1fr;
    gap: 85px;
  }
}

.contentContainer {
  color: var(--gray-8);
  text-align: center;

  @include media('sm') {
    text-align: left;
  }
}

.headerContainer {
  max-width: 544px;
  margin: 0 auto 22px;

  @include media('sm') {
    margin-left: 0;
  }
}

.subheader {
  @include fluid-type(16px, 22px);
  font-family: var(--georgia);
  line-height: math.div(32, 22);
}

.copy {
  @include fluid-type(16px, 19px);
  line-height: math.div(30, 19);
  margin-bottom: 20px;
}

.cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 26px 40px;
  margin-bottom: 40px;

  @include media('sm') {
    grid-template-columns: 1fr 1fr;
  }
}

.card {
  --padding--base: 0;
  --padding--lg: 0;
}

.cardLayout {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 14px;
}

.mediaHeader {
  text-align: left;
  font-weight: bold;
}

.cta {
  margin-top: 40px;
}

.cardContainer {
  display: grid;
  gap: 19px;
}
</style>
